import { DeleteOutlined, EyeOutlined } from "@ant-design/icons"
import { Flex, Tag, Typography } from "antd"
import type { ItemType } from "antd/es/menu/interface"
import { type ColumnsType } from "antd/es/table"
import React from "react"

import Icon from "../../../../../../../Icon"
import { RowActionsMenu } from "../../../../../../../components/EntityTable/RowActionsMenu.tsx"
import { empty } from "../../../../../../../constants/placeholders"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import { tableGuideAnchor } from "../../../../../../../utils/table-guide-anchor.ts"
import { INVOICE_EMAIL_ATTACHMENT_MAX_MB } from "../../../../../../../utils/attachment-upload.ts"
import {
  type Attachment,
  AttachmentModels
} from "../../../../../../Album/types/attachment.types.ts"
import { invoiceColumnTitle } from "../../../../../../Invoices/components/invoice-column-titles.tsx"
import { InvoiceEditButton } from "../../../../../../Invoices/components/InvoiceEditButton.tsx"
import { InvoiceRowActions } from "../../../../../../Invoices/helpers/invoice-row-actions.tsx"
import { INVOICE_ORIGINS } from "../../../../../../Invoices/types/invoices.types.ts"
import { AttachmentsController } from "../../../../../components/AttachmentsController.tsx"
import type { ProjectInvoice } from "../../../../../types/project-invoices.types.tsx"
import {
  INVOICE_STATUSES_MAP,
  type InvoiceStatuses
} from "../../../../constants/invoice-statuses.ts"
import { getInvoicePermissions } from "../../../../../../Invoices/helpers/get-invoice-permissions.tsx"

const { Text } = Typography

type Translate = (key: string, options?: Record<string, unknown>) => string

// Shared with InvoiceEditRow, which matches its cells to these
// columns by key rather than by position.
export const INVOICE_COLUMN_KEYS = {
  invoiceNumber: "invoice_number",
  customerName: "customer_name",
  dateIssued: "date_issued",
  dueDate: "due_date",
  amount: "amount",
  originDisplay: "origin_display",
  paidToDate: "paid_to_date",
  status: "status",
  assigneeName: "assignee_name",
  attachments: "attachments"
} as const

interface GetColumnsColumnsArgs {
  t: Translate
  currentUserId: string
  isLocked: boolean
  projectId: string
  //
  createAllowed?: boolean
  deleteAllowed?: boolean
  deleteForeignAllowed?: boolean
  editAllowed?: boolean
  editForeignAllowed?: boolean
  voidAllowed?: boolean
  //
  uploadFileAllowed?: boolean
  deleteFileAllowed?: boolean
  deleteForeignFileAllowed?: boolean
  //
  onSelectRow: (record: ProjectInvoice, action: string) => void
}

export const getColumns = ({
  t,
  currentUserId,
  onSelectRow,
  projectId,
  createAllowed,
  deleteAllowed,
  deleteForeignAllowed,
  editAllowed,
  editForeignAllowed,
  voidAllowed,
  uploadFileAllowed,
  deleteFileAllowed,
  deleteForeignFileAllowed,
  isLocked
}: GetColumnsColumnsArgs): ColumnsType<ProjectInvoice> => [
  {
    title: "Invoice ID",
    dataIndex: "invoice_number",
    key: "invoice_number",
    width: 140,
    render: (text: string) => <Text strong>{text || empty}</Text>
  },
  {
    title: t("invoices.columns.customer"),
    dataIndex: "customer_name",
    key: "customer_name",
    width: 180,
    ellipsis: true,
    render: (name: string | null) => name || empty
  },
  {
    title: "Date Issued",
    dataIndex: "date_issued",
    key: "date_issued",
    sorter: true,
    width: 130
  },
  {
    title: "Due Date",
    dataIndex: "due_date",
    key: "due_date",
    sorter: true,
    width: 130,
    onHeaderCell: () => tableGuideAnchor("invoice-col-due-date")
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    width: 140,
    onHeaderCell: () => tableGuideAnchor("invoice-col-amount"),
    render: (value: string) => <Text>{budgetFormat(value)}</Text>
  },
  {
    title: invoiceColumnTitle(t, "origin"),
    dataIndex: "origin_display",
    key: "origin_display",
    width: 120,
    render: (label: string | undefined, record) =>
      label ? (
        <Tag
          color={
            record?.origin === INVOICE_ORIGINS.GENERATED ? "blue" : "default"
          }
          className="m-0"
        >
          {label}
        </Tag>
      ) : (
        empty
      )
  },
  {
    title: invoiceColumnTitle(t, "paidToDate"),
    dataIndex: "paid_to_date",
    key: "paid_to_date",
    width: 130,
    render: (value: string | undefined) =>
      value != null ? <Text>{budgetFormat(value)}</Text> : empty
  },
  {
    title: invoiceColumnTitle(t, "status"),
    dataIndex: "status",
    key: "status",
    width: 140,
    onHeaderCell: () => tableGuideAnchor("invoice-col-status"),
    render: (status: InvoiceStatuses, record) => {
      const statusData = INVOICE_STATUSES_MAP?.[status]

      return (
        <Tag style={statusData?.style} className="m-0">
          {record?.status_display || statusData?.label || empty}
        </Tag>
      )
    }
  },
  {
    title: "Assignee",
    dataIndex: "assignee_name",
    key: "assignee_name",
    width: 180,
    ellipsis: true,
    render: (name: string | null) => name || empty
  },
  {
    title: "Attachments",
    key: "attachments",
    dataIndex: "attachments",
    width: 100,
    align: "center",
    render: (attachments: Attachment[], record) => (
      <AttachmentsController
        currentUserId={currentUserId}
        addAllowed={uploadFileAllowed}
        deleteAllowed={deleteFileAllowed}
        deleteForeignAllowed={deleteForeignFileAllowed}
        project_id={projectId}
        model_type={AttachmentModels.INVOICE}
        object_id={record?.id}
        attachments={attachments}
        note={
          record?.origin === INVOICE_ORIGINS.EXTERNAL
            ? t("finance.quickAddInvoice.attachments.emailLimit", {
                size: INVOICE_EMAIL_ATTACHMENT_MAX_MB
              })
            : undefined
        }
      />
    )
  },
  {
    title: "Actions",
    width: 110,
    align: "center",
    hidden:
      !editAllowed &&
      !createAllowed &&
      !editForeignAllowed &&
      !deleteAllowed &&
      !deleteForeignAllowed,
    fixed: "right",
    render: (_, record) => {
      const {
        canDelete,
        canDownloadPdf,
        canPreview,
        hasEditRights,
        isLocked: isImmutable
      } = getInvoicePermissions({
        record,
        currentUserId,
        deleteInvoice: deleteAllowed,
        deleteForeignInvoice: deleteForeignAllowed,
        editInvoice: editAllowed,
        editForeignInvoice: editForeignAllowed
      })

      const isGenerated =
        record?.origin === INVOICE_ORIGINS.GENERATED ||
        Boolean(record?.is_generated)

      const editButton = hasEditRights ? (
        <InvoiceEditButton
          record={record}
          t={t}
          disabled={isLocked}
          onEdit={() => onSelectRow(record, "edit_drawer")}
        />
      ) : null

      if (isGenerated || isImmutable) {
        return (
          <Flex gap="small" justify="center" align="center">
            {editButton}
            <InvoiceRowActions
              record={record}
              t={t}
              editAllowed={hasEditRights}
              deleteAllowed={canDelete}
              voidAllowed={voidAllowed}
              disabled={isLocked}
              onSelectRow={(action) => onSelectRow(record, action)}
            />
          </Flex>
        )
      }

      if (!editButton && !canDelete && !canDownloadPdf && !canPreview) {
        return null
      }

      return (
        <Flex gap={"small"} justify={"center"} align="center">
          {editButton}
          {(canDelete || canDownloadPdf || canPreview) && (
          <RowActionsMenu
            disabled={isLocked}
            items={
              [
                {
                  key: "preview_row",
                  label: t("invoices.actions.preview"),
                  hidden: !canPreview,
                  icon: <EyeOutlined />,
                  onClick: () => onSelectRow(record, "preview_row")
                },
                {
                  key: "download_pdf",
                  label: t("invoices.actions.download"),
                  hidden: !canDownloadPdf,
                  icon: <Icon type={"Download"} />,
                  onClick: () => onSelectRow(record, "download_pdf")
                },
                {
                  key: "delete_row",
                  label: "Delete",
                  hidden: !canDelete,
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => onSelectRow(record, "delete_row")
                }
              ] as Array<ItemType & { hidden?: boolean }>
            }
          />
          )}
        </Flex>
      )
    }
  }
]
